import type { ECharts } from 'echarts/core';
import { Chart } from '../../../../ui-kit';
import type { HeatmapPayload, HeatmapRow } from '../../../../types/plannerDashboard';
import { percent, whole } from '../plannerFormat';

/** The campaigns a trend can be drawn for, biggest first. */
export function trendRows(payload: HeatmapPayload | null): HeatmapRow[] {
  return [...(payload?.rows ?? [])].sort((a, b) => b.base - a.base);
}

export const trendKey = (r: { channel: string; campaign: string }) => `${r.channel}|${r.campaign}`;

/**
 * The Trend view of the campaign grid: one campaign's rate across the grid's
 * columns — each week, month or day of the week — set against every campaign
 * together, so a change to that campaign shows as its line leaving the other
 * (Lisa, 30 Sep 2026).
 *
 * It is a view of the grid rather than a chart of its own, so the grid's
 * steps, cohort and minimum visibly apply to it. A column resting on too few
 * leads is a gap, as the grid greys it.
 */
export default function CampaignTrendChart({
  payload, row, fill, onReady,
}: { payload: HeatmapPayload; row: HeatmapRow; fill?: boolean; onReady?: (chart: ECharts) => void }) {
  const fromStage = payload.stages.find((s) => s.key === payload.from);
  const toStage = payload.stages.find((s) => s.key === payload.to);
  const cohorts = payload.by !== 'dow';
  // Cohorts are listed newest first in the grid; a trend reads left to right.
  const columns = cohorts ? [...payload.columns].reverse() : payload.columns;
  const cells = new Map(row.cells.map((c) => [c.key, c]));
  const everyone = new Map((payload.column_totals ?? []).map((c) => [c.key, c]));
  const counts = (hits: number, base: number) => `${whole(hits)} ${toStage?.label ?? ''} of ${whole(base)} ${fromStage?.label ?? ''}`;
  const few = columns.length <= 16;

  const option = {
    grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
    legend: { top: 0, left: 0 },
    tooltip: {
      trigger: 'axis',
      formatter: (items: { dataIndex: number }[]) => {
        const col = columns[items[0].dataIndex];
        const line = (name: string, c?: { base: number; hits: number; rate: number | null }) =>
          !c || c.base === 0
            ? `${name}: none`
            : `${name}: <b>${c.rate === null ? 'not enough to tell' : percent(c.rate)}</b> (${counts(c.hits, c.base)})`;
        return [
          `<b>${col.name}</b>${col.partial ? ' (part of it)' : ''}`,
          line('This campaign', cells.get(col.key)),
          line('All campaigns', everyone.get(col.key)),
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: columns.map((c) => c.short_name),
      axisTick: { show: false },
      axisLabel: { hideOverlap: true },
    },
    yAxis: { type: 'value', min: 0, axisLabel: { formatter: '{value}%' } },
    series: [
      {
        name: row.campaign,
        type: 'line',
        connectNulls: false,
        showSymbol: true,
        symbolSize: 8,
        lineStyle: { width: 2 },
        data: columns.map((c) => cells.get(c.key)?.rate ?? null),
        label: few
          ? { show: true, position: 'top', formatter: (p: { value: number | null }) => (p.value === null ? '' : percent(p.value)) }
          : { show: false },
        labelLayout: { hideOverlap: true },
      },
      {
        name: 'All campaigns',
        type: 'line',
        connectNulls: false,
        showSymbol: true,
        symbolSize: 6,
        lineStyle: { width: 2, type: 'dashed' },
        data: columns.map((c) => everyone.get(c.key)?.rate ?? null),
      },
    ],
  };
  return (
    <Chart
      option={option}
      height={fill ? undefined : 360}
      fill={fill}
      onReady={onReady}
      ariaLabel={`${toStage?.label ?? ''} as a share of ${fromStage?.label ?? ''} for ${row.campaign}, by ${cohorts ? payload.unit : 'day of week'}, against all campaigns`}
    />
  );
}
